import type { BatchQuoteResult } from 'epic/batchWorkspace/pricingConflationEpics';
import type { IdType } from 'epic/common-form/ReloadFormRequest';
import type { BatchImportMetadataResponse } from 'models/batch/import-metadata';
import type { FormError } from 'models/formError';
import { ProcessingChain } from 'models/ProcessingChain';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { DataById, FormDataStateMap } from 'state/formData/formData.model';
import type { StaticProductView } from 'state/product-description/types';
import { QuoteResult } from 'state/quotes/results/result.model';
import { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { BatchTabContentFormDetails } from 'state/workspaces/batch/tabContent/tabContent.model';
import type { ImportFormRequest } from 'utils/import/import.models';
import { FileParseResult } from 'utils/import/utils/parseFileUtils';

import type { BatchFeesAndMetricsDataSet } from '.';
import type { QuoteAction } from '../common-form/infos/infos.model';
import type { FieldMetadataById, FormFieldMetadataStateMap } from '../common-form/metaData/fields/fields.model';
import * as actionTypes from './batchForm.actionTypes';

export type BatchFormAction = FormActions | QuoteActions | TradeActions;

export type BatchFormReloadCorrelation = {
  operationId: string;
  quoteId: string;
  scheduledRunId: string;
};

export type FormActions =
  | DefaultBatchFormRequested
  | DefaultBatchFormReceived
  | DefaultBatchFormFailed
  | DefaultBatchFormTimeouted
  | BatchFormEnrichmentReceived
  | BatchFormEnrichmentFailed
  | BatchFormSelectToggled
  | BatchFormSelectAllToggled
  | BatchFormsDeleteRequested
  | BatchFormsDeleted
  | BatchFormReloadRequested
  | BatchFormReloadFailed
  | BatchFormsCloneRequested
  | BatchFormQuoteReloadRequested
  | BatchQuotePushBackCancelRequested
  | BatchFormsRestoreRequested
  | BatchFormRollbackRequested
  | BatchImportMetadataRequested
  | BatchImportMetadataReceived
  | BatchImportMetadataFailed
  | BatchImportFormRequested
  | BatchFeesAndMetricsExpansionToggled
  | BatchFeesAndMetricsDataSetChanged;

export type QuoteActions =
  | BatchQuoteAddToQueue
  | BatchQuoteRequested
  | BatchQuoteRequestFailed
  | BatchQuoteAcked
  | BatchQuoteReceived
  | BatchQuoteRemoved
  | BatchQuoteResultFailed
  | BatchQuoteAborted
  | BatchQuoteExpired
  | BatchPreviousQuoteLoaded
  | BatchPreviousQuoteLoadingFailed;
// | BatchQuoteDenied

export type TradeActions = BatchTradeAuthReceived | BatchTradeAuthTimeout;

export function defaultBatchFormRequested(
  tabId: string,
  temporaryFormId: string,
  productType: string,
  productSubType: string,
  wrapper: string,
  payoffType?: string,
  variationParameters?: Record<string, unknown>,
) {
  return {
    type: actionTypes.DEFAULT_BATCH_FORM_REQUESTED,
    tabId,
    temporaryFormId,
    productType,
    productSubType,
    wrapper,
    payoffType,
    variationParameters,
  };
}
export type DefaultBatchFormRequested = ReturnType<typeof defaultBatchFormRequested>;

export function defaultBatchFormReceived(
  tabId: string,
  temporaryFormId: string,
  negotiationId: string,
  productView: StaticProductView,
  actions: QuoteAction[],
  values: DataById,
  fieldsMetadata: FieldMetadataById,
  quoteQuery: unknown,
  version: number,
  mustForceAdvancedMode: boolean,
  validationErrors?: FormError[],
  reloadCorrelation?: BatchFormReloadCorrelation,
) {
  return {
    type: actionTypes.DEFAULT_BATCH_FORM_RECEIVED,
    tabId,
    temporaryFormId,
    negotiationId,
    productView,
    actions,
    values,
    fieldsMetadata,
    quoteQuery,
    version,
    validationErrors,
    mustForceAdvancedMode,
    ...(reloadCorrelation === undefined ? {} : { reloadCorrelation }),
  };
}
export type DefaultBatchFormReceived = ReturnType<typeof defaultBatchFormReceived>;

export function defaultBatchFormFailed(temporaryFormId: string) {
  return {
    type: actionTypes.DEFAULT_BATCH_FORM_FAILED,
    temporaryFormId,
  };
}
export type DefaultBatchFormFailed = ReturnType<typeof defaultBatchFormFailed>;

export function defaultBatchFormTimeouted(tabId: string) {
  return {
    type: actionTypes.DEFAULT_BATCH_FORM_TIMEOUTED,
    tabId,
  };
}
export type DefaultBatchFormTimeouted = ReturnType<typeof defaultBatchFormTimeouted>;

export function batchFormEnrichmentReceived(
  formId: string,
  productSubtype: string,
  productView: StaticProductView,
  fieldsMetadata: FieldMetadataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  validationErrors?: FormError[],
) {
  return {
    type: actionTypes.BATCH_FORM_ENRICHMENT_RECEIVED,
    productSubtype,
    formId,
    productView,
    fieldsMetadata,
    version,
    actions,
    quoteQuery,
    validationErrors,
    mustForceAdvancedMode,
  };
}
export type BatchFormEnrichmentReceived = ReturnType<typeof batchFormEnrichmentReceived>;

export function batchFormEnrichmentFailed(tabId: string, formId: string, pendingData: DataById) {
  return {
    type: actionTypes.BATCH_FORM_ENRICHMENT_FAILED,
    tabId,
    formId,
    pendingData,
  };
}
export type BatchFormEnrichmentFailed = ReturnType<typeof batchFormEnrichmentFailed>;

export function batchFormSelectToggled(formId: string) {
  return {
    type: actionTypes.BATCH_FORM_SELECT_TOGGLED,
    formId,
  };
}
export type BatchFormSelectToggled = ReturnType<typeof batchFormSelectToggled>;

export function batchFormSelectAllToggled(value: boolean, formIds: readonly string[]) {
  return {
    type: actionTypes.BATCH_FORM_SELECT_ALL_TOGGLED,
    value,
    formIds,
  };
}
export type BatchFormSelectAllToggled = ReturnType<typeof batchFormSelectAllToggled>;

export function batchFormsDeleteRequested(formIds: readonly string[]) {
  return {
    type: actionTypes.BATCH_FORMS_DELETE_REQUESTED,
    formIds,
  };
}
export type BatchFormsDeleteRequested = ReturnType<typeof batchFormsDeleteRequested>;

export function batchFormsDeleted(tabId: string, formIds: readonly string[]) {
  return { type: actionTypes.BATCH_FORMS_DELETED, tabId, formIds };
}
export type BatchFormsDeleted = ReturnType<typeof batchFormsDeleted>;

export function batchFormReloadRequested(
  tabId: string,
  temporaryFormId: string,
  identifier: string,
  idType: IdType,
  tradeDate: string,
  productType: string,
  productSubType: string,
  wrapper: string,
  reloadCorrelation?: BatchFormReloadCorrelation,
) {
  return {
    type: actionTypes.BATCH_FORM_RELOAD_REQUESTED,
    tabId,
    temporaryFormId,
    identifier,
    idType,
    tradeDate,
    productType,
    productSubType,
    wrapper,
    ...(reloadCorrelation === undefined ? {} : { reloadCorrelation }),
  };
}
export type BatchFormReloadRequested = ReturnType<typeof batchFormReloadRequested>;

export function batchFormReloadFailed(
  temporaryFormId: string,
  tabId?: string,
  reloadCorrelation?: BatchFormReloadCorrelation,
) {
  return {
    type: actionTypes.BATCH_FORM_RELOAD_FAILED,
    temporaryFormId,
    ...(tabId === undefined ? {} : { tabId }),
    ...(reloadCorrelation === undefined ? {} : { reloadCorrelation }),
  };
}
export type BatchFormReloadFailed = ReturnType<typeof batchFormReloadFailed>;

export function batchQuoteAddToQueue(tabId: string, formIds: readonly string[]) {
  return { type: actionTypes.BATCH_QUOTE_ADD_TO_QUEUE, tabId, formIds };
}
export type BatchQuoteAddToQueue = ReturnType<typeof batchQuoteAddToQueue>;

export function batchQuoteRequested(formIds: readonly string[], quoteRequestStartingTimeInMillis: number) {
  return { type: actionTypes.BATCH_QUOTE_REQUESTED, formIds, quoteRequestStartingTimeInMillis };
}
export type BatchQuoteRequested = ReturnType<typeof batchQuoteRequested>;

export function batchQuoteAcked(
  tabId: string | undefined,
  forms: readonly QuoteAck[],
  quoteRequestStartingTimeInMillis: number,
) {
  return {
    type: actionTypes.BATCH_QUOTE_ACKED,
    tabId,
    forms,
    quoteRequestStartingTimeInMillis,
  };
}
export type BatchQuoteAcked = ReturnType<typeof batchQuoteAcked>;

export function batchQuoteRemoved(tabId: string, formIds: readonly string[]) {
  return {
    type: actionTypes.BATCH_QUOTE_REMOVED,
    tabId,
    formIds,
  };
}

export type BatchQuoteRemoved = ReturnType<typeof batchQuoteRemoved>;

export function batchQuoteRequestFailed(forms: ReadonlyArray<{ formId: string; newVersion?: number }>) {
  return { type: actionTypes.BATCH_QUOTE_REQUEST_FAILED, forms };
}
export type BatchQuoteRequestFailed = ReturnType<typeof batchQuoteRequestFailed>;

export function batchQuoteReceived(tabId: string, formId: string, quoteId: string, quote: BatchQuoteResult) {
  return {
    type: actionTypes.BATCH_QUOTE_RECEIVED,
    formId,
    quoteId,
    tabId,
    quote,
  };
}
export type BatchQuoteReceived = ReturnType<typeof batchQuoteReceived>;

export function batchQuoteExpired(formId: string) {
  return {
    type: actionTypes.BATCH_QUOTE_EXPIRED,
    formId,
  };
}
export type BatchQuoteExpired = ReturnType<typeof batchQuoteExpired>;

export function batchQuoteResultFailed(formId: string, quoteId: string) {
  return {
    type: actionTypes.BATCH_QUOTE_RESULT_FAILED,
    formId,
    quoteId,
  };
}
export type BatchQuoteResultFailed = ReturnType<typeof batchQuoteResultFailed>;

export function batchQuotePushBackCancelRequested(formId: string) {
  return { type: actionTypes.BATCH_QUOTE_PUSH_BACK_CANCEL_REQUESTED, formId };
}
export type BatchQuotePushBackCancelRequested = ReturnType<typeof batchQuotePushBackCancelRequested>;

export function batchFormsCloneRequested(
  tabId: string,
  formsToBeCloned: FormDataStateMap,
  formsMetadataToBeCloned: FormFieldMetadataStateMap,
  temporaryFormIds: readonly string[],
  productType: string,
  productSubType: string,
  wrapper: string,
  mustForceAdvancedMode: boolean,
) {
  return {
    type: actionTypes.BATCH_FORMS_CLONE_REQUESTED,
    tabId,
    formsToBeCloned,
    formsMetadataToBeCloned,
    temporaryFormIds,
    productType,
    productSubType,
    wrapper,
    mustForceAdvancedMode,
  };
}
export type BatchFormsCloneRequested = ReturnType<typeof batchFormsCloneRequested>;

export function batchFormQuoteReloadRequested(
  tabId: string,
  temporaryFormId: string,
  id: string,
  idType: IdType,
  productType: string,
  productSubType: string,
  wrapper: string,
  quoteId: string,
  pricerRfqId?: string,
  processingChain?: ProcessingChain,
) {
  return {
    type: actionTypes.BATCH_FORM_QUOTE_RELOAD_REQUESTED,
    tabId,
    temporaryFormId,
    id,
    idType,
    productType,
    productSubType,
    wrapper,
    quoteId,
    pricerRfqId,
    processingChain,
  };
}
export type BatchFormQuoteReloadRequested = ReturnType<typeof batchFormQuoteReloadRequested>;

export function batchFormsRestoreRequested(
  tabId: string,
  formIds: readonly string[],
  forms: readonly BatchTabContentFormDetails[],
  productType: string,
  productSubType: string,
  wrapper: string,
) {
  return {
    type: actionTypes.BATCH_FORMS_RESTORE_REQUESTED,
    tabId,
    formIds,
    forms,
    productType,
    productSubType,
    wrapper,
  };
}
export type BatchFormsRestoreRequested = ReturnType<typeof batchFormsRestoreRequested>;

export function batchFormRollbackRequested(tabId: string, formId: string) {
  return {
    type: actionTypes.BATCH_FORM_ROLLBACK_REQUESTED,
    formId,
    tabId,
  };
}

export type BatchFormRollbackRequested = ReturnType<typeof batchFormRollbackRequested>;

export const batchQuoteAborted = (formId: string, quoteId?: string) => ({
  type: actionTypes.BATCH_QUOTE_ABORTED,
  formId,
  quoteId,
});
export type BatchQuoteAborted = ReturnType<typeof batchQuoteAborted>;

export function batchTradeAuthReceived(
  quoteId: string,
  { status, rejections, expirationDelayInSeconds }: NextTradeAuthorizationResult,
  when: Date,
) {
  return {
    type: actionTypes.BATCH_TRADE_AUTH_RECEIVED,
    quoteId,
    status,
    rejections,
    expirationDelayInSeconds,
    when,
  };
}
export type BatchTradeAuthReceived = ReturnType<typeof batchTradeAuthReceived>;

export function batchTradeAuthTimeout(quoteId: string) {
  return {
    type: actionTypes.BATCH_TRADE_AUTH_TIMEOUT,
    quoteId,
  };
}
export type BatchTradeAuthTimeout = ReturnType<typeof batchTradeAuthTimeout>;

export function batchPreviousQuoteLoaded(formId: string, quoteId: string, quote: QuoteResult) {
  return {
    type: actionTypes.BATCH_PREVIOUS_QUOTE_LOADED,
    formId,
    quoteId,
    quote,
  };
}

export type BatchPreviousQuoteLoaded = ReturnType<typeof batchPreviousQuoteLoaded>;

export function batchPreviousQuoteLoadingFailed(tabId: string, quoteId: string) {
  return {
    type: actionTypes.BATCH_PREVIOUS_QUOTE_LOADING_FAILED,
    tabId,
    quoteId,
  };
}
export type BatchPreviousQuoteLoadingFailed = ReturnType<typeof batchPreviousQuoteLoadingFailed>;

export function batchImportMetadataRequested(fileParsedResult: FileParseResult) {
  return {
    type: actionTypes.BATCH_IMPORT_METADATA_REQUESTED,
    fileParsedResult,
  };
}
export type BatchImportMetadataRequested = ReturnType<typeof batchImportMetadataRequested>;

export function batchImportMetadataReceived(metadata: BatchImportMetadataResponse) {
  return {
    type: actionTypes.BATCH_IMPORT_METADATA_RECEIVED,
    metadata,
  };
}
export type BatchImportMetadataReceived = ReturnType<typeof batchImportMetadataReceived>;

export function batchImportMetadataFailed(error: string) {
  return {
    type: actionTypes.BATCH_IMPORT_METADATA_FAILED,
    error,
  };
}
export type BatchImportMetadataFailed = ReturnType<typeof batchImportMetadataFailed>;

export function batchImportFormRequested(
  tabId: string,
  temporaryFormId: string,
  productType: string,
  productSubType: string,
  wrapper: string,
  request: ImportFormRequest,
) {
  return {
    type: actionTypes.BATCH_IMPORT_FORM_REQUESTED,
    tabId,
    temporaryFormId,
    productType,
    productSubType,
    wrapper,
    request,
  };
}
export type BatchImportFormRequested = ReturnType<typeof batchImportFormRequested>;

export function batchFeesAndMetricsExpansionToggled() {
  return {
    type: actionTypes.BATCH_FEES_AND_METRICS_EXPANSION_TOGGLED,
  };
}
export type BatchFeesAndMetricsExpansionToggled = ReturnType<typeof batchFeesAndMetricsExpansionToggled>;

export function batchFeesAndMetricsDataSetChanged(dataSet: BatchFeesAndMetricsDataSet) {
  return {
    type: actionTypes.BATCH_FEES_AND_METRICS_DATA_SET_CHANGED,
    dataSet,
  };
}
export type BatchFeesAndMetricsDataSetChanged = ReturnType<typeof batchFeesAndMetricsDataSetChanged>;
